"use client"

import { useState, useEffect } from "react"
import { motion } from "framer-motion"
import { Newspaper, Calendar, ChevronRight, ArrowUpRight } from "lucide-react"
import Link from "next/link"

export function LatestNews() {
    const [news, setNews] = useState<any[]>([])
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        const fetchNews = async () => {
            try {
                const res = await fetch("/api/news?limit=3")
                if (res.ok) {
                    const data = await res.json()
                    setNews(data.news || [])
                }
            } catch (error) {
                console.error("Failed to fetch news")
            } finally {
                setLoading(false)
            }
        }
        fetchNews()
    }, [])

    if (loading) return null

    return (
        <section className="py-24 container mx-auto px-4">
            <div className="flex items-center justify-between mb-12">
                <div>
                    <h2 className="text-4xl font-black text-white tracking-tight mb-2 uppercase">Latest Chronicles</h2>
                    <p className="text-slate-400 font-medium italic">Updates from the world of DragonL2</p>
                </div>
                <Link href="/news" className="group flex items-center gap-2 text-blue-400 font-bold uppercase tracking-widest text-xs hover:text-blue-300 transition-colors">
                    View Archive
                    <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {news.map((item, idx) => (
                    <motion.div
                        key={item.nid}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.1 }}
                        className="group relative bg-slate-900/40 border border-slate-800/50 rounded-2xl overflow-hidden hover:border-blue-500/50 transition-all hover:shadow-2xl hover:shadow-blue-500/10"
                    >
                        {/* Image Header */}
                        <div className="h-48 w-full overflow-hidden relative">
                            <img
                                src={item.img || "https://images.unsplash.com/photo-1542751371-adc38448a05e?auto=format&fit=crop&q=80&w=800"}
                                alt={item.title_en || item.title_pt}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-transparent" />
                            <div className="absolute top-4 left-4">
                                <span className="px-3 py-1 bg-blue-500/80 backdrop-blur-md rounded-full text-[10px] font-black text-white uppercase tracking-widest">
                                    Update
                                </span>
                            </div>
                        </div>

                        {/* Content */}
                        <div className="p-6">
                            <div className="flex items-center gap-2 text-slate-500 text-xs font-bold uppercase tracking-widest mb-4">
                                <Calendar className="w-3.5 h-3.5" />
                                {new Date(item.post_date * 1000).toLocaleDateString()}
                            </div>
                            <h3 className="text-xl font-bold text-white mb-4 line-clamp-2 leading-tight group-hover:text-blue-400 transition-colors">
                                {item.title_en || item.title_pt}
                            </h3>
                            <p className="text-slate-400 text-sm leading-relaxed line-clamp-3 mb-6">
                                {(item.content_en || item.content_pt)?.replace(/<[^>]*>/g, '')}
                            </p>

                            <Link
                                href={`/news/${item.nid}`}
                                className="inline-flex items-center gap-2 text-white font-bold text-xs uppercase tracking-widest group/link"
                            >
                                Read Chronicle
                                <ArrowUpRight className="w-3.5 h-3.5 text-slate-500 group-hover/link:text-white group-hover/link:-translate-y-0.5 group-hover/link:translate-x-0.5 transition-all" />
                            </Link>
                        </div>
                    </motion.div>
                ))}
            </div>
        </section>
    )
}
